/*
 * --------------------------------------
 * Copyright (c) 2020 BetterCapital.US
 * --------------------------------------
 */

import React, { PureComponent, Fragment } from "react";
import PropTypes from "prop-types";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { saveAsset } from "../../../actions/asset/assetAction";
import { withRouter } from "react-router";

import FormField from "../../../shared/components/form/FormField";
import customSelect from "../../../shared/components/form/customSelect";
import ToggleSwitch from "../../../shared/components/form/ToggleSwitch";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { connect } from "react-redux";

const assetRoles = [
  { value: "O", label: "Owner" },
  { value: "I", label: "Investor" },
  { value: "T", label: "Tenant" },
  { value: "IA", label: "Insurance Agent" },
  { value: "R", label: "Real Estate Agent" },
  { value: "C", label: "Certified Public Accountant" },
  { value: "B", label: "Banker" },
  { value: "U", label: "Other" },
  { value: "PM", label: "Property Manager" }
];

class AssetImport extends PureComponent {
  static propTypes = {
    data: PropTypes.object.isRequired,
    modalOpen: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.state = {
      importFailed: false,
      loading: false
    };
  }

  handleSubmit = (values, { props = this.props }) => {
    values.areaSquareFeet |= 0;
    values.lotSizeSquareFeet |= 0;
    props.saveAsset(values);
    this.setState({ loading: true });
  };

  closeModal = () => {
    this.props.toggleModal();
    this.props.toggleMainModal();
  };

  getAllowedRoles = type => {
    switch (type) {
      case "rental":
        return assetRoles.filter(a => ["O", "T", "PM"].includes(a.value));
      case "note":
        return assetRoles.filter(a => ["O", "I"].includes(a.value));
      case "syndication":
        return assetRoles.filter(a => ["O", "I"].includes(a.value));
      case "flip":
      case "for_sale":
        return assetRoles.filter(a => ["O", "R"].includes(a.value));
      case "fund":
        return assetRoles.filter(a => ["I"].includes(a.value));
      default:
        return assetRoles.filter(a => ["O"].includes(a.value));
    }
  };

  componentWillReceiveProps = nextProps => {
    const { asset } = nextProps;
    if (
      this.state.loading &&
      asset.isSavingAssetSuccess === true &&
      asset.slug
    ) {
      this.setState({ loading: false }, () => {
        this.closeModal();
        nextProps.history.push(`/assets/${asset.slug}/financials`);
      });
    } else if (asset.isSavingAssetSuccess === false) {
      this.setState({ importFailed: true, loading: false });
    }
  };

  render() {
    const { data } = this.props;
    let initialValues = data;
    initialValues.user_role = "O";
    initialValues.owner_email = "";
    initialValues.tenant_email = "";
    initialValues.assetType = "primary";
    initialValues.self_managed = true;
    initialValues.management_platform = "";

    const validationSchema = Yup.object({
      user_role: Yup.string("").required("Asset Role is required"),
      owner_email: Yup.string().when("user_role", {
        is: role => role === "O",
        then: Yup.string().email("Invalid Email"),
        otherwise: Yup.string()
          .email("Invalid Email")
          .required("Owner email is required")
          .max(100, "Email too long")
      }),
      assetType: Yup.string("").required("Asset Type is required")
    });

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={this.handleSubmit}
      >
        {props => {
          const { handleSubmit, values } = props;
          const { modalOpen } = this.props;
          return (
            <Modal
              isOpen={modalOpen}
              className={"modal-dialog modal-dialog-centered"}
              contentClassName={"newexpensemodal"}
            >
              <ModalHeader toggle={this.closeModal}>
                <h5 className="modal-title">{"Import Asset"}</h5>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  <div className="form-group row">
                    <label htmlFor="type" className="col-sm-2 col-form-label">
                      Type:
                    </label>
                    <div className="col-sm-10 inputblock">
                      <Field
                        name="assetType"
                        component={customSelect}
                        placeholder="Asset Type"
                        options={[
                          { value: "primary", label: "Primary Residence" },
                          { value: "rental", label: "Rental" },
                          { value: "note", label: "Note/Trust Deed" },
                          { value: "syndication", label: "Syndication" },
                          { value: "flip", label: "Fix & Flip" },
                          { value: "fund", label: "Fund" },
                          { value: "for_sale", label: "For Sale" }
                        ]}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label htmlFor="role" className="col-sm-2 col-form-label">
                      Role:
                    </label>
                    <div className="col-sm-10 inputblock">
                      <Field
                        name="user_role"
                        component={customSelect}
                        placeholder="Asset Role"
                        options={this.getAllowedRoles(values.assetType)}
                      />
                    </div>
                  </div>
                  {values.user_role !== "O" && (
                    <div className="form-group row">
                      <label
                        htmlFor="staticEmail"
                        className="col-sm-2 col-form-label"
                      >
                        Owner's Email:
                      </label>
                      <div className="col-sm-10 inputblock">
                        <Field
                          name="owner_email"
                          component={FormField}
                          type="email"
                          placeholder="example@example.com"
                        />
                      </div>
                    </div>
                  )}
                  {values.assetType === "rental" && values.user_role === "O" && (
                    <Fragment>
                      <div className="form-group row">
                        <label
                          htmlFor="staticEmail"
                          className="col-sm-2 col-form-label"
                        >
                          Tenant's Email:
                        </label>
                        <div className="col-sm-10 inputblock">
                          <Field
                            name="tenant_email"
                            component={FormField}
                            type="email"
                            placeholder="example@example.com"
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label
                          htmlFor="self_managed"
                          className="col-sm-2 col-form-label"
                        >
                          Self Managed:
                        </label>
                        <div className="col-sm-10 inputblock">
                          <Field
                            name="self_managed"
                            component={ToggleSwitch}
                            placeholder="Self Managed"
                          />
                        </div>
                      </div>
                    </Fragment>
                  )}
                  <div className="form-group row">
                    <label
                      htmlFor="management_platform"
                      className="col-sm-2 col-form-label"
                    >
                      Platform:
                    </label>
                    <div className="col-sm-10 inputblock">
                      <Field
                        name="management_platform"
                        component={customSelect}
                        placeholder="Select Platform"
                        options={[
                          { value: "none", label: "None" },
                          { value: "airbnb", label: "AirBnB" },
                          { value: "appfolio", label: "AppFolio" },
                          { value: "buildium", label: "Buildium" },
                          { value: "hemlane", label: "Hemlane" },
                          { value: "quickbook", label: "QuickBooks" },
                          { value: "propertyware", label: "PropertyWare" },
                          { value: "rentmanager", label: "RentManager" },
                          { value: "cozy", label: "Cozy.co" },
                          { value: "other", label: "Other" }
                        ]}
                      />
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <button
                    type="reset"
                    className="btn btn-secondary px-5 d-inline-block mx-2"
                    onClick={this.closeModal}
                    disabled={this.state.loading}
                  >
                    Cancel
                  </button>
                  <button
                    color="primary"
                    type="submit"
                    className="btn savebtns btn-primary px-5  d-inline-block mx-2"
                    disabled={this.state.loading}
                  >
                    Save{" "}
                  </button>
                </ModalFooter>
              </form>
            </Modal>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = state => ({
  asset: state.asset
});

const mapDispatchToProps = dispatch => ({
  saveAsset: asset => saveAsset(asset, dispatch)
});
export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(AssetImport)
);
